import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AdminSettingsService } from '@lmc-app/core/services/admin-settings.service';
import {
	merge,
	shareReplay,
	Subject,
	Subscription,
	switchMap,
	timer,
} from 'rxjs';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import {
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import {
	MatSelect,
	MatSelectTrigger,
	MatOption,
} from '@angular/material/select';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

export interface NLSUsers {
	username: string;
	loginDate: string;
	loginAddress: string;
}

const ELEMENT_DATA: NLSUsers[] = [];

@Component({
	selector: 'app-nls-settings',
	templateUrl: './nls-settings.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: [
		`
			.top-container {
				display: flex;
				align-items: flex-start; /* Aligns both to the top edge */
				gap: 10px; /* Space between them */
				margin-bottom: 2em;
			}
			p,
			button {
				display: inline-block;
				vertical-align: top; /* Forces top alignment on the line */
			}
			p {
				font-size: 1.25rem;
			}
			.row-disabled {
				opacity: 0.5;
				pointer-events: none; /* Prevents clicks on the row itself */
				background-color: #f5f5f5;
			}
			mat-select,
			mat-option {
				font-size: 14px;
				width: 150px;
			}
			label {
				text-transform: uppercase;
			}
		`,
	],
	imports: [
		NgIf,
		MatButton,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		MatSelect,
		ReactiveFormsModule,
		FormsModule,
		MatSelectTrigger,
		NgFor,
		MatOption,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		NgClass,
		AsyncPipe,
	],
})
export class NlsSettingsComponent implements OnInit {
	private reload$ = new Subject<void>(); // Reload signal for nls user sessions
	private reloadToken$ = new Subject<void>(); // Reload signal for nls user sessions
	private nlsSubscription: Subscription;

	displayedColumns: string[] = [
		'position',
		'username',
		'loginDate',
		'loginAddress',
		'actions',
	];
	dataSource = ELEMENT_DATA;
	isActive = true;
	statuses = [
		{ value: 'Active', label: 'Active', isDisabled: false },
		{ value: 'Disconnect', label: 'Disconnect', isDisabled: false }, // Set true to disable
		{ value: 'In progress', label: 'In progress', isDisabled: true },
	];

	nlsToken$ = merge(timer(0, 60000), this.reloadToken$).pipe(
		switchMap(() => this.adminSettingService.validateNlsToken()),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(
		private readonly adminSettingService: AdminSettingsService,
		private readonly apiService: LegacyApiService
	) {}
	ngOnInit() {
		this.nlsSubscription = merge(
			timer(0, 60000), // Auto-reload every 60s
			this.reload$
		)
			.pipe(switchMap(() => this.adminSettingService.activeNLSsessions()))
			.subscribe((data) => {
				this.dataSource = data;
			});
	}

	/**
	 *
	 * @param element
	 * @returns signout the nls user
	 */
	onDisconnect(element: any) {
		if (element.status === 'Disconnect') {
			this.adminSettingService
				.disconnectNLSsessions(element?.session_id, element?.userno)
				.subscribe({
					next: (res) => {
						this.reload$.next();
					},
					error: (err) => console.error('Disconnect failed', err),
				});
		} else {
			return;
		}
	}

	/**
	 *
	 * @param status Based on status the color code
	 * @returns
	 */
	getStatusColor(status: string) {
		switch (status) {
			case 'Active':
				return 'green';
			case 'In progress':
				return 'orange';
			case 'Disconnect':
				return 'red';
			default:
				return 'black';
		}
	}

	/**
	 * Generate NLS token
	 */
	generateNewNLSToken() {
		this.apiService.generateNewNLSToken().subscribe({
			next: (data) => {
				console.log('This is now a string:', data);
				this.reloadToken$;
			},
			error: (err) => console.error(err),
		});
	}

	ngOnDestroy() {
		this.nlsSubscription?.unsubscribe();
	}
}
